// src/hooks/useNavigationCounts.ts
import { useState, useEffect, useCallback } from 'react';

interface NavigationCounts {
  products: number;
  stores: number;
  tags: number;
  batches: number;
  campaigns: number;
  analytics: number;
  shipments: number;
}

interface UseNavigationCountsReturn {
  counts: NavigationCounts | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refresh: () => void;
}

// API base URL configuration
// Production: https://api.jeptags.com/api  (endpoints below use paths WITHOUT /api prefix)
// Localhost:   /api                         (relative, same origin)
const getApiBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_BASE_URL || process.env.VITE_API_URL || '';
  if (envUrl) return envUrl;

  // Auto-detect from hostname
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'jeptags.com' || host === 'www.jeptags.com' || host.endsWith('.jeptags.com')) {
      return 'https://api.jeptags.com/api';
    }
  }
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Helper function to format large numbers
export const formatCount = (count: number): string => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
};

export const useNavigationCounts = (): UseNavigationCountsReturn => {
  const [counts, setCounts] = useState<NavigationCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Helper function to get auth token
  const getAuthToken = (): string | null => {
    return localStorage.getItem('businessToken') || 
           localStorage.getItem('auth_token') || 
           localStorage.getItem('token') ||
           null;
  };

  // Helper function to make API calls with auth
  const fetchWithAuth = async (endpoint: string, token: string): Promise<any> => {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  };

  // Helper function to fetch with fallback
  const fetchWithFallback = async (primaryEndpoint: string, fallbackEndpoint: string, token: string): Promise<any> => {
    try {
      return await fetchWithAuth(primaryEndpoint, token);
    } catch (error) {
      console.warn(`Failed to fetch ${primaryEndpoint}, trying ${fallbackEndpoint}`);
      return await fetchWithAuth(fallbackEndpoint, token);
    }
  };

  // Main function to fetch all counts
  const fetchCounts = useCallback(async () => {
    try {
      console.log('🔄 Fetching navigation counts...');
      setLoading(true);
      setError(null);
      
      // Check if we have a token
      const token = getAuthToken();
      if (!token) {
        console.warn('No authentication token found, using fallback counts');
        throw new Error('Not authenticated');
      }

      // Endpoints WITHOUT /api prefix — API_BASE_URL already includes /api
      const results = await Promise.allSettled([
        fetchWithAuth('/products', token),
        fetchWithAuth('/stores', token),
        fetchWithFallback('/tags/libraries', '/tags', token),
        fetchWithAuth('/batches', token),
        fetchWithAuth('/dashboard/stats', token),
        fetchWithFallback('/supply-chain/events', '/shipments', token),
        fetchWithAuth('/campaigns', token)
      ]);

      // Process results — REAL API format: { success, products: [...], pagination: { total } }
      // Items are at ROOT level, not nested under .data
      const newCounts: NavigationCounts = {
        products: 0,
        stores: 0,
        tags: 0,
        batches: 0,
        campaigns: 0,
        analytics: 0,
        shipments: 0
      };

      // Extract products count
      // API returns: { success, products: [...], pagination: { total: 4 } }
      if (results[0].status === 'fulfilled') {
        const resp = results[0].value;
        if (resp?.success !== false) {
          newCounts.products = resp?.pagination?.total 
            || resp?.summary?.total
            || (Array.isArray(resp?.products) ? resp.products.length : 0)
            || (Array.isArray(resp?.data?.products) ? resp.data.products.length : 0);
        }
      }

      // Extract stores count
      // API returns: { success, stores: [...], pagination: { total } }
      if (results[1].status === 'fulfilled') {
        const resp = results[1].value;
        if (resp?.success !== false) {
          newCounts.stores = resp?.pagination?.total 
            || (Array.isArray(resp?.stores) ? resp.stores.length : 0)
            || (Array.isArray(resp?.data?.stores) ? resp.data.stores.length : 0);
        }
      }

      // Extract tags count
      // API returns: { success, libraries: [...] } or { success, tags: [...] }
      if (results[2].status === 'fulfilled') {
        const resp = results[2].value;
        if (resp?.success !== false) {
          const items = resp?.libraries || resp?.tags || resp?.data?.libraries || resp?.data?.tags;
          if (Array.isArray(items)) {
            const isLibraries = items.length > 0 && items[0] && 'tagCount' in items[0];
            if (isLibraries) {
              newCounts.tags = items.reduce((total: number, library: any) => {
                return total + (library.tagCount || library.tags?.length || 0);
              }, 0);
            } else {
              newCounts.tags = resp?.pagination?.total || items.length;
            }
          }
        }
      }

      // Extract batches count
      // API returns: { success, batches: [...], pagination: { total } }
      if (results[3].status === 'fulfilled') {
        const resp = results[3].value;
        if (resp?.success !== false) {
          newCounts.batches = resp?.pagination?.total
            || (Array.isArray(resp?.batches) ? resp.batches.length : 0)
            || (Array.isArray(resp?.data?.batches) ? resp.data.batches.length : 0);
        }
      }

      // Extract analytics count from dashboard stats
      if (results[4].status === 'fulfilled') {
        const data = results[4].value;
        if (data?.success !== false) {
          const stats = data?.data || data;
          newCounts.analytics = stats?.analytics?.newInsights || 
                               stats?.analytics?.totalEvents ||
                               stats?.recentActivity?.length || 0;
        }
      }

      // Extract shipments count
      if (results[5].status === 'fulfilled') {
        const resp = results[5].value;
        if (resp?.success !== false) {
          const items = resp?.shipments || resp?.events || resp?.data?.shipments || resp?.data;
          if (Array.isArray(items)) {
            newCounts.shipments = resp?.pagination?.total || items.length;
          }
        }
      }

      // Extract campaigns count
      if (results[6].status === 'fulfilled') {
        const resp = results[6].value;
        if (resp?.success !== false) {
          const items = resp?.campaigns || resp?.data?.campaigns || resp?.data;
          newCounts.campaigns = resp?.pagination?.total || (Array.isArray(items) ? items.length : 0);
        }
      }

      setCounts(newCounts);
      setLastUpdated(new Date());
      
      console.log('✅ Navigation counts updated:', newCounts);
      
    } catch (err) {
      console.error('⚠️ Error fetching navigation counts:', err);
      
      // Use zero values on error — don't show fake numbers
      setCounts({
        products: 0,
        stores: 0,
        tags: 0,
        batches: 0,
        campaigns: 0,
        analytics: 0,
        shipments: 0
      });
      
      setError(err instanceof Error ? err.message : 'Failed to fetch counts');
    } finally {
      setLoading(false);
    }
  }, []);

  // Manual refresh function
  const refresh = () => {
    fetchCounts();
  };

  // Set up automatic updates
  useEffect(() => {
    // Initial load
    fetchCounts();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchCounts, 30000);

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, [fetchCounts]);

  return { counts, loading, error, lastUpdated, refresh };
};

export default useNavigationCounts;